import { useTranslation } from 'react-i18next';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ModalHeaderProps } from '../../utils/types';

/**
 * Shared header for the Manage API Keys modal views: title + close button,
 * with an optional back link and subtitle. Uses the standard MFE dialog title
 * styling (inherited from DialogTitle).
 *
 * With `onBack` the title moves below the back link; otherwise it sits inline
 * with the close button.
 */
const ModalHeader = ({
  title,
  onClose,
  subtitle = undefined,
  learnMoreHref = undefined,
  learnMoreText = undefined,
  onBack = undefined,
  icon = undefined,
}: ModalHeaderProps) => {
  const { t } = useTranslation('aiTooling', { keyPrefix: 'apiKeys.modal' });

  // `component="span"` keeps the title valid inline content inside the <h2>
  // that DialogTitle renders.
  const titleNode = (
    <Stack component="span" direction="row" spacing={1} alignItems="center">
      {icon}
      <Typography component="span" variant="inherit">
        {title}
      </Typography>
    </Stack>
  );

  return (
    <DialogTitle>
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          {onBack ? (
            <Button
              variant="text"
              size="small"
              startIcon={<ChevronLeftIcon />}
              onClick={onBack}
              sx={{ ml: -1 }}
              data-testid="modal-back-btn"
            >
              {t('back')}
            </Button>
          ) : (
            titleNode
          )}
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="close"
            data-testid="manage-api-keys-close-btn"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {onBack && titleNode}
        {subtitle && (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="modal-header-description"
          >
            {subtitle}
            {learnMoreHref && learnMoreText && (
              <>
                <Link
                  sx={{ ml: 0.5 }}
                  href={learnMoreHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  data-testid="modal-header-learn-more"
                >
                  {learnMoreText}
                </Link>
              </>
            )}
          </Typography>
        )}
      </Stack>
    </DialogTitle>
  );
};

export default ModalHeader;
